import { Component, computed, ElementRef, forwardRef, HostListener, input, numberAttribute, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';


const SLIDER_VALUE_ACCESSOR_PROVIDER = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SliderComponent),
    multi: true,
};

@Component({
    selector: 'ui-slider',
    providers: [SLIDER_VALUE_ACCESSOR_PROVIDER],
    styles: [
        `
        :host {
            display: block;
            line-height: 1em;
            min-width: 0;
            --handle-color: var(--brand);
            --handle-color-focused: #c2dbfe;
            --handle-inside-color-focused: #b6d4fe;
            --track-color: #dee2e6;
            --handle-color-disabled: #cad0d5;
            --track-color-disabled: #ecf1f4;
            --outline-color: var(--secondary)

        }
        .host {
            position: relative;
            height: calc(1em + 4px);
            vertical-align: middle;
        }

        .track {
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            height: 4px;
            transform: translateY(-50%);
            border-radius: 2px;
            background: var(--track-color);
            cursor: pointer;
            touch-action: none;
        }

        .track-fill {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background: var(--brand);
            border-radius: 2px;
            background: var(--handle-color);
            pointer-events: none;
        }

        .host.disabled .track {
            background: var(--track-color-disabled);
            cursor: default;
        }

        .host.disabled .track-fill {
            background: var(--handle-color-disabled);
        }

        .handle {
            position: absolute;
            height: 18px;
            width: 18px;
            border-radius: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            background: var(--handle-color);
            touch-action: none;
            transition:
                transform 0.15s ease,
                box-shadow 0.15s ease;
        }

        .handle::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 14px;
            height: 14px;
            background: transparent;
            border-radius: 50%;
            transform: translate(-50%, -50%);
            transition: background 0.15s ease;

        }

        .host.disabled .handle {
            background: var(--handle-color-disabled);
        }

        .host:focus .handle {
            transform: translate(-50%, -50%) scale(1.05);
            box-shadow:
                0 0 0 1px white,
                0 0 0 4px var(--handle-color-focused);
        }

        .host.handle-active .handle {
            transform: translate(-50%, -50%) scale(1.05);
            cursor: grabbing;
            box-shadow:
                0 0 0 1px white,
                0 0 0 4px var(--handle-color-focused);
        }

        .host.handle-active .handle::after {
            background: var(--handle-inside-color-focused);
        }

        .no-select {
            user-select: none;
        }
        `,
    ],
    template: `
        <div
            class="host"
            role="slider"
            [class.disabled]="isDisabled()"
            [class.handle-active]="handleActive()"
            [attr.tabindex]="isDisabled() ? null : '0'"
            [attr.aria-valuemin]="min()"
            [attr.aria-valuemax]="max()"
            [attr.aria-valuenow]="value()"
            [attr.aria-disabled]="isDisabled() ? 'true' : null">
            <div class="track"
                #trackEl
                (mousedown)="onTrackMouseDown($event)"
                (touchstart)="onTrackTouchStart($event)">
                <div class="track-fill" [style.width.%]="percent()"></div>
                <div class="handle"
                    #handleEl
                    [style.left.%]="percent()"
                    (mousedown)="onHandleMouseDown($event)"
                    (touchstart)="onHandleTouchStart($event)"></div>
            </div>
        </div>
    `,
})
export class SliderComponent implements ControlValueAccessor {

    min = input(0, { transform: numberAttribute });

    max = input(100,{ transform: numberAttribute });

    step = input(1, { transform: numberAttribute });

    value = signal(0);

    isDisabled = signal(false);

    handleActive = signal(false);

    percent = computed(() => {
        const min = this.min();
        const max = this.max();
        const value = this.value();

        const span = max - min;

        if (span <= 0) {
            return 0;
        }

        const clamped = Math.min(max, Math.max(min, value));
        return ((clamped - min) / span) * 100;
    });

    private dragging = false;

    private dragOffsetPx = 0;

    private trackEl = viewChild.required<ElementRef<HTMLElement>>('trackEl');

    private handleEl = viewChild.required<ElementRef<HTMLElement>>('handleEl');

    private onChange = (_value: number) => {};

    private onTouched = () => {};

    private updateValue(nextValue: number) {
        const clamped = Math.min(this.max(), Math.max(this.min(), nextValue));
        this.value.set(clamped);
        this.onChange(clamped);
    }

    private setValueFromPageX(pageX: number) {
        const rect = this.trackEl().nativeElement.getBoundingClientRect();
        const trackLeft = rect.left + window.scrollX;
        const ratio = (pageX - trackLeft) / rect.width;
        const clampedRatio = Math.min(1, Math.max(0, ratio));

        const raw = this.min() + clampedRatio * (this.max() - this.min());
        const snapped = this.min() + Math.round((raw - this.min()) / this.step()) * this.step();

        this.updateValue(snapped);
    }

    private beginDrag(handleActive = false) {
        if(this.isDisabled()) {
            return false;
        }
        this.dragging = true;
        this.handleActive.set(handleActive);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
        return true;
    }

    private endDrag(markTouched = true) {
        if (markTouched && this.dragging) {
            this.onTouched();
        }
        this.dragging = false;
        this.handleActive.set(false);
        this.dragOffsetPx = 0;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }


    @HostListener('document:mousemove', ['$event'])
    onMouseMove(e: MouseEvent) {
        if (!this.dragging) {
            return;
        }

        this.setValueFromPageX(e.pageX - this.dragOffsetPx);
    }

    @HostListener('document:mouseup')
    onMouseUp() {
        this.endDrag();
    }

    @HostListener('document:touchmove', ['$event'])
    onTouchMove(e: TouchEvent) {
        if (!this.dragging || e.touches.length === 0) {
            return;
        }

        this.setValueFromPageX(e.touches[0].pageX - this.dragOffsetPx);
        e.preventDefault();
    }

    @HostListener('document:touchend')
    onTouchEnd() {
        this.endDrag();
    }

    @HostListener('document:touchcancel')
    onTouchCancel() {
        this.endDrag(false);
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(e: KeyboardEvent) {
        if (this.isDisabled()) {
            return;
        }

        const currentValue = this.value();
        let nextValue: number | null = null;

        switch (e.key) {

            case 'ArrowRight':
            case 'ArrowUp':
                nextValue = currentValue + this.step();
                e.preventDefault();
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                nextValue = currentValue - this.step();
                e.preventDefault();
                break;
            case 'PageUp': {
                const largeStep = Math.max(this.step() * 10, (this.max() - this.min()) * 0.1);
                nextValue = currentValue + largeStep;
                e.preventDefault();
                break;
            }
            case 'PageDown': {
                const largeDec = Math.max(this.step() * 10, (this.max() - this.min()) * 0.1);
                nextValue = currentValue - largeDec;
                e.preventDefault();
                break;
            }
            case 'Home':
                nextValue = this.min();
                e.preventDefault();
                break;
            case 'End':
                nextValue = this.max();
                e.preventDefault();
                break;
        }

        if (nextValue !== null) {
            this.updateValue(nextValue);
            this.onTouched();
        }
    }

    onTrackMouseDown(e: MouseEvent) {
        if (!this.beginDrag()) {
            return;
        }
        this.dragOffsetPx = 0;
        this.setValueFromPageX(e.pageX);
    }

    onHandleMouseDown(e: MouseEvent) {
        if (!this.beginDrag(true)) {
            return;
        }

        const rect = this.handleEl().nativeElement.getBoundingClientRect();
        const handleCenter = rect.left + window.scrollX + rect.width / 2;

        this.dragOffsetPx = e.pageX - handleCenter;

        e.preventDefault();
        e.stopPropagation();
    }

    onTrackTouchStart(e: TouchEvent) {
        if (!this.beginDrag() || e.touches.length === 0) {
            return;
        }

        this.dragOffsetPx = 0;
        this.setValueFromPageX(e.touches[0].pageX);
        e.preventDefault();
    }

    onHandleTouchStart(e: TouchEvent) {
        if (!this.beginDrag(true) || e.touches.length === 0) {
            return;
        }

        const rect = this.handleEl().nativeElement.getBoundingClientRect();
        const handleCenter = rect.left + window.scrollX + rect.width / 2;

        this.dragOffsetPx = e.touches[0].pageX - handleCenter;

        e.stopPropagation();
        e.preventDefault();
    }

    writeValue(obj: unknown): void {
        const n = Number(obj);
        const next = Number.isFinite(n) ? n : this.min();
        this.value.set(Math.min(this.max(), Math.max(this.min(), next)));
    }

    registerOnChange(fn: (value: number) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.isDisabled.set(isDisabled);
    }
}
